import React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';

const RegisterSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <Header />
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Container maxWidth="sm" sx={{ width: '80%' }}>
        <Paper
          elevation={24}
          sx={{
            p: 6,
            borderRadius: 3,
            background: 'rgba(26, 26, 46, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
          }}
        >
          {/* Success Icon */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #28A0F0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 50, color: '#ffffff' }} />
          </Box>

          {/* Success Message */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('auth.register.success.title')}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              mb: 3,
              fontWeight: 500,
            }}
          >
            {t('auth.register.success.subtitle')}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            {t('auth.register.success.message')}
          </Typography>

          {/* Pending Approval Alert */}
          <Alert
            severity="warning"
            icon={<EmailIcon />}
            sx={{
              mb: 4,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              color: '#ffffff',
              '& .MuiAlert-icon': {
                color: '#f59e0b',
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Вашият акаунт очаква одобрение от администратор
            </Typography>
            <Typography variant="body2">
              Ще получите имейл, когато акаунтът ви бъде одобрен. До тогава няма да можете да влезете в системата.
            </Typography>
          </Alert>

          {/* What's Next */}
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 2,
              p: 3,
              mb: 4,
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#ffffff',
                mb: 2,
                fontWeight: 600,
              }}
            >
              {t('auth.register.success.whatsNext')}
            </Typography>
            <Box sx={{ textAlign: 'left' }}>
              {(t('auth.register.success.steps', { returnObjects: true }) as string[]).map((step: string, index: number) => (
                <Typography key={index} variant="body2" sx={{ color: '#d0d0d0', mb: 1 }}>
                  ✓ {step}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              sx={{
                borderColor: '#2d2d44',
                color: '#ffffff',
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#28A0F0',
                  backgroundColor: 'rgba(40, 160, 240, 0.1)',
                },
              }}
            >
              {t('auth.register.success.homePage')}
            </Button>
          </Box>
        </Paper>
      </Container>
      </Box>
    </Box>
  );
};

export default RegisterSuccessPage;
